import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './Auth.css';

const SSO_URL = process.env.REACT_APP_SSO_URL ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:4000'
    : `https://sso.cixio.ai`);

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const loginWithSSO = () => {
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);
    const state = Math.random().toString(36).substring(7);
    const clientId = 'cixio-in';

    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_redirect_from', returnUrl || '/');

    const authUrl = `${SSO_URL}/authorize?` +
      `response_type=code` +
      `&client_id=${clientId}` +
      `&redirect_uri=${redirectUri}` +
      `&state=${state}` +
      `&scope=openid profile email`;

    window.location.href = authUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      toast.success('Login successful!');
      
      // If there's a returnUrl, redirect there
      if (returnUrl) {
        navigate(returnUrl);
      } else {
        // Redirect based on user type
        const userType = result.user?.userType;
        if (userType === 'support' || userType === 'admin') {
          navigate('/support-dashboard');
        } else {
          navigate('/');
        }
      }
    } else {
      toast.error(result.message);
    }
    
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Cixio</h1>
          <p>End-to-End Software Solutions — B2B & B2C</p>
        </div>

        <h2>Welcome back</h2>

        {/* SSO Login Button */}
        <button
          type="button"
          onClick={loginWithSSO}
          className="btn btn-block"
          style={{
            background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
            color: 'white',
            padding: '14px 24px',
            border: 'none',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: '700',
            cursor: 'pointer',
            width: '100%',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)',
            letterSpacing: '0.01em',
          }}
        >
          🚀 Login with CIXIO SSO
        </button>

        <div style={{
          textAlign: 'center',
          margin: '20px 0',
          position: 'relative',
          color: '#94a3b8',
          fontSize: '13px',
          fontWeight: '500',
        }}>
          <span style={{
            background: 'rgba(255,255,255,0.95)',
            padding: '0 16px',
            position: 'relative',
            zIndex: 1,
          }}>OR</span>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '1px',
            background: '#e2e8f0',
          }} />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              className="form-control"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              name="password"
              className="form-control"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>

          <div style={{ textAlign: 'right', marginBottom: '16px' }}>
            <Link to="/forgot-password" style={{ fontSize: '14px', color: '#6366f1', textDecoration: 'none', fontWeight: 500 }}>
              Forgot Password?
            </Link>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="auth-footer">
          <p>Don't have an account? <Link to="/register">Register here</Link></p>
        </div>
      </div>
    </div>
  );
};

export default Login;
